/**
 * Aperçu de l'import CSV.
 *
 * L'onglet « Importer un CSV » n'affichait rien avant l'import, contrairement à
 * l'onglet Tradovate : l'utilisateur découvrait le résultat une fois les trades
 * dans son journal. Il importait aussi sans commission, si bien qu'un fichier
 * Tradovate terminé à -60 entrait à 0 $.
 *
 * Ce module fournit le même résumé que la modale Tradovate, à partir des trades
 * déjà parsés, pour que les deux chemins annoncent le même chiffre.
 *
 * Convention du projet : la commission est stockée NÉGATIVE, et
 * netPnl = pnl + commission + swaps (cf. tradePhase.ts).
 */

import type { ParsedTrade } from "./csvImport";

export type CsvPreviewSummary = {
  count: number;
  longs: number;
  shorts: number;
  /** P&L net, commissions et swaps compris. */
  totalPnl: number;
};

export function summarizeCsvTrades(trades: ParsedTrade[]): CsvPreviewSummary {
  return trades.reduce<CsvPreviewSummary>(
    (acc, trade) => {
      acc.count += 1;
      if (trade.type === "long") acc.longs += 1;
      else acc.shorts += 1;
      acc.totalPnl += (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
      return acc;
    },
    { count: 0, longs: 0, shorts: 0, totalPnl: 0 },
  );
}

/**
 * Applique un taux de commission par contrat aux trades qui n'en portent pas.
 *
 * Un export Tradovate ne détaille pas ses frais : sans ce calcul, ils sont
 * simplement perdus. Un CSV natif ou TopstepX porte en revanche ses propres
 * montants, qu'il ne faut pas écraser par une estimation.
 *
 * @param ratePerContract Taux général. Signe ignoré : un taux est un coût.
 * @param rateForSymbol Taux propre au symbole, tiré du template de la prop
 *   firm. Il prime sur le taux général, une prop firm facturant rarement le
 *   micro et le mini au même prix.
 */
export function applyCommissionRate(
  trades: ParsedTrade[],
  ratePerContract: number,
  rateForSymbol?: (symbol: string) => number | null,
): ParsedTrade[] {
  const fallback = Number.isFinite(ratePerContract) ? Math.abs(ratePerContract) : 0;
  if (fallback === 0 && !rateForSymbol) return trades;

  return trades.map((trade) => {
    if (trade.commission) return trade;

    const symbolRate = rateForSymbol?.(trade.symbol) ?? null;
    const raw = symbolRate ?? fallback;
    const rate = Number.isFinite(raw) ? Math.abs(raw) : 0;
    if (rate === 0) return trade;

    return { ...trade, commission: -rate * (trade.quantity || 0) };
  });
}
